import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ArrowRight, ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkerLogin = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!phone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/worker/send-otp`, { phone: phone.trim() });
      toast.success("OTP sent to your phone! (Use 1234 for testing)");
      setStep("otp");
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send OTP";
      toast.error(typeof msg === "string" ? msg : "Failed to send OTP");
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length < 4) {
      toast.error("Please enter the 4-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/worker/verify-otp`, { phone: phone.trim(), otp }, { withCredentials: true });
      const token = res.data.token;
      localStorage.setItem("junction_token", token);
      localStorage.setItem("junction_worker", JSON.stringify(res.data.worker));
      toast.success(`Welcome back, ${res.data.worker.name}!`);
      navigate("/worker-dashboard");
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid OTP";
      toast.error(typeof msg === "string" ? msg : "Verification failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="worker-login-page">
      <div className="w-full max-w-md">
        <a href="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-10 h-10 rounded-xl bg-indigo flex items-center justify-center">
            <span className="text-white font-heading font-bold text-xl">J</span>
          </div>
          <span className="font-heading font-semibold text-2xl text-indigo tracking-tight">Junction</span>
        </a>

        <div className="bg-white rounded-3xl p-8 shadow-sm border border-gray-50">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-marigold/10 flex items-center justify-center mx-auto mb-4">
              {step === "phone" ? <Phone size={24} className="text-marigold" /> : <KeyRound size={24} className="text-marigold" />}
            </div>
            <h1 className="font-heading text-2xl font-semibold text-indigo mb-2">
              {step === "phone" ? "Worker Login" : "Verify OTP"}
            </h1>
            <p className="text-sm text-gray-500">
              {step === "phone" ? "Enter your registered phone number" : `OTP sent to ${phone}`}
            </p>
          </div>

          {step === "phone" ? (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Phone Number</label>
                <Input
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="rounded-xl bg-cream-alt border-none h-12 focus-visible:ring-marigold"
                  data-testid="worker-login-phone-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-indigo text-white hover:bg-indigo/90 rounded-full py-6 font-medium text-base group" data-testid="worker-login-send-otp">
                {loading ? "Sending..." : "Send OTP"}
                <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div className="flex justify-center">
                <InputOTP maxLength={4} value={otp} onChange={setOtp} data-testid="worker-login-otp-input">
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <p className="text-xs text-center text-gray-400">Testing OTP: 1234</p>
              <Button type="submit" disabled={loading} className="w-full bg-marigold text-indigo hover:bg-marigold/90 rounded-full py-6 font-medium text-base" data-testid="worker-login-verify-otp">
                {loading ? "Verifying..." : "Verify & Login"}
              </Button>
              <button type="button" onClick={() => { setStep("phone"); setOtp(""); }} className="w-full text-center text-sm text-gray-500 hover:text-indigo flex items-center justify-center gap-1">
                <ArrowLeft size={14} />Change phone number
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          Not registered?{" "}
          <a href="/#register" className="text-terracotta hover:underline">Register as a worker</a>
        </p>
      </div>
    </div>
  );
};

export default WorkerLogin;
